<template>
  <v-container class="id-verification">
    <HkiaraceBannerFrame :label="$t('id_verification.title')">
      <v-container v-if="tokenVerified">
        <v-window v-model="step">
          <v-window-item>
            <v-form ref="stepTwoForm">
              <v-row>
                <v-col class="text-h6">{{ $t('id_verification.description') }}</v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-radio-group v-model="idType"
                                 column
                                 :rules="[value => !!value || $t('register.required_fields')]">
                    <v-radio :value="2" :label="$t('id_type.airport_octopus')"></v-radio>
                    <v-radio :value="4" :label="$t('id_type.personal_octopus')"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row>
                <v-col sm="12" lg="6">
                  <v-text-field outlined
                                :label="$t('id_verification.suffix_number')"
                                placeholder="A12345678(9)"
                                v-model="idNumber"
                                :rules="[value => !!value || $t('register.required_fields')]"></v-text-field>
                </v-col>
              </v-row>
            </v-form>
            <v-row>
              <v-col cols="12" lg="6">
                <v-row>
                  <v-col class="pb-0">
                    {{ $t('register.octopus_front') }}
                  </v-col>
                </v-row>
                <PhotoPreviewInput v-model="octopusFront"
                                   div-class="octopus-front"
                                   file-name="octopus-front"></PhotoPreviewInput>
              </v-col>
              <v-col cols="12" lg="6">
                <v-row>
                  <v-col class="pb-0">
                    {{ $t('register.octopus_back') }}
                  </v-col>
                </v-row>
                <PhotoPreviewInput v-model="octopusBack"
                                   div-class="octopus-back"
                                   file-name="octopus-back"></PhotoPreviewInput>
              </v-col>
            </v-row>

            <v-row v-if="idType === 4">
              <v-col cols="12" lg="6">
                <v-row>
                  <v-col class="pb-0">
                    {{ $t('register.id_front') }}
                  </v-col>
                </v-row>
                <PhotoPreviewInput v-model="idFront"
                                   div-class="id-front"
                                   file-name="id-front"></PhotoPreviewInput>
              </v-col>
              <v-col cols="12" lg="6">
                <v-row>
                  <v-col class="pb-0">
                    {{ $t('register.id_back') }}
                  </v-col>
                </v-row>
                <PhotoPreviewInput v-model="idBack"
                                   div-class="id-back"
                                   file-name="id-back"></PhotoPreviewInput>
              </v-col>
            </v-row>


            <v-row>
              <v-col class="text-right">
                <v-btn dark
                       rounded
                       x-large
                       color="primary"
                       class="function-button"
                       @click="submit">
                  {{ $t('next') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-window-item>
          <v-window-item>
            <v-container>
              <v-row>
                <v-col>
                  {{ $t('register.success_msg') }}
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn dark
                         color="primary"
                         rounded
                         x-large
                         class="register-btn function-button"
                         @click="$router.push({name: 'home'})">
                    BACK TO HOME
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-window-item>
        </v-window>
      </v-container>
      <v-container v-if="tokenInvalid">
        <v-row>
          <v-col>
            {{ $t('email_verify.error_msg') }}
          </v-col>
        </v-row>
      </v-container>
    </HkiaraceBannerFrame>
  </v-container>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core'
import {faCameraRetro} from '@fortawesome/free-solid-svg-icons'
import HkiaraceBannerFrame from "../../../components/HkiaraceBannerFrame";
import PhotoPreviewInput from "@/components/PhotoPreviewInput";
import {ref, onMounted} from "@vue/composition-api";

library.add(faCameraRetro);

export default {
  name: 'IdVerification',
  components: {PhotoPreviewInput, HkiaraceBannerFrame},
  setup(props, {root}) {
    const step = ref(0);
    const tokenVerified = ref(false);
    const tokenInvalid = ref(false);

    // Step 2
    const stepTwoForm = ref(null);
    const idType = ref(null);
    const idNumber = ref(null);

    const getURL = function (f) {
      return URL.createObjectURL(f);
    };

    const octopusFront = ref(null);
    const octopusBack = ref(null);
    const idFront = ref(null);
    const idBack = ref(null);

    const submit = async function () {
      if (!stepTwoForm.value.validate()) {
        root.$vuetify.goTo('.hkiarace-banner-frame', {easing: 'easeInOutCubic'});
        return
      }

      if (!octopusFront.value) {
        root.$vuetify.goTo('.octopus-front', {easing: 'easeInOutCubic', offset: 250});
        return
      }

      if (!octopusBack.value) {
        root.$vuetify.goTo('.octopus-back', {easing: 'easeInOutCubic', offset: 250});
        return
      }

      if (idType.value === 4 && !idFront.value) {
        root.$vuetify.goTo('.id-front', {easing: 'easeInOutCubic', offset: 250});
        return
      }

      if (idType.value === 4 && !idBack.value) {
        root.$vuetify.goTo('.id-back', {easing: 'easeInOutCubic', offset: 250});
        return
      }

      root.$callAPI('hkiarace/id_verify/', {
        token: root.$route.params.token,
        id_type: idType.value,
        id_no: idNumber.value,
        octopus_front: await root.$convertFileToBase64(octopusFront.value),
        octopus_back: await root.$convertFileToBase64(octopusBack.value),
        id_front: idType.value === 4 ? await root.$convertFileToBase64(idFront.value) : null,
        id_back: idType.value === 4 ? await root.$convertFileToBase64(idBack.value) : null,
      }, () => {
        step.value = 1;
      })
    };

    onMounted(() => {
      root.$callAPI('hkiarace/id_verify_token/', {token: root.$route.params.token}, () => {
        tokenVerified.value = true;
      }, () => {
        tokenInvalid.value = true;
      })
    })

    return {
      step,
      tokenVerified,
      tokenInvalid,

      stepTwoForm,
      idType,
      idNumber,
      getURL,
      octopusFront,
      octopusBack,
      idFront,
      idBack,
      submit,
    }
  }
}
</script>

<style lang="less">
.id-verification {
  .photo-preview-input-div {
    &::before {
      content: "";
      opacity: 0.15;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      position: absolute;
    }

    &.ara-permit-front::before {
      background: url('../../../assets/ara-front.png');
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
    }

    &.octopus-front::before {
      background: url('../../../assets/octopus-front.png');
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
    }

    &.ara-permit-back::before {
      background: url('../../../assets/ara-back.png');
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
    }

    &.octopus-back::before {
      background: url('../../../assets/octopus-back.png');
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
    }
  }
}
</style>
